import type { CosmosDestination, CosmosRoute, CosmosRouteSpace } from '@/_contract/cosmosRoute';

export function parseCosmosRoute(pathname: string): CosmosRoute {
    const parts = pathname.replace(/\/$/, '').split('/').slice(1);
    if (!pathname.startsWith('/') || parts.some((part) => part === '')) return { kind: 'invalid' };
    if (parts[0] !== 'cosmos') return { kind: 'invalid' };
    let decoded: string[];
    try {
        decoded = parts.map(decodeURIComponent);
    } catch {
        return { kind: 'invalid' };
    }
    if (decoded.length === 1) return { kind: 'index' };
    if (decoded[1] === 'episode') return decoded.length === 3
        ? { kind: 'episode', episodeHandle: decoded[2] } : { kind: 'invalid' };
    if (decoded.length === 2) return { kind: 'space', spaceSlug: decoded[1] };
    if (decoded.length === 4 && decoded[2] === 'episode') {
        return { kind: 'space', spaceSlug: decoded[1], episodeHandle: decoded[3] };
    }
    return { kind: 'invalid' };
}

export function cosmosSpaceUrl(spaceSlug: string, episodeHandle?: string): string {
    if (!spaceSlug || episodeHandle === '') throw new Error('Cosmos URLs require nonempty space and episode identifiers.');
    return `/cosmos/${encodeURIComponent(spaceSlug)}${episodeHandle ? `/episode/${encodeURIComponent(episodeHandle)}` : ''}`;
}

/** Query parameter naming the route a viewer left through a portal. */
export const ROUTE_ORIGIN_PARAM = 'from';

export function withRouteOrigin(destination: string, origin: string): string {
    return `${destination}?${ROUTE_ORIGIN_PARAM}=${encodeURIComponent(origin)}`;
}

export function resolveCosmosDestination(route: CosmosRoute, spaces: readonly CosmosRouteSpace[],
    defaultSpaceSlug: string): CosmosDestination {
    if (route.kind === 'invalid') return { error: 'Invalid Cosmos route.' };
    if (route.kind === 'episode') {
        const matches = spaces.filter((space) => space.episodeHandles.includes(route.episodeHandle));
        if (matches.length !== 1) return { error: 'Episode is unavailable or has an ambiguous space reference.' };
        return { spaceSlug: matches[0].spaceSlug, episodeHandle: route.episodeHandle };
    }
    const spaceSlug = route.kind === 'space' ? route.spaceSlug : defaultSpaceSlug;
    const space = spaces.find((candidate) => candidate.spaceSlug === spaceSlug);
    if (!space) return { error: 'Unknown Cosmos space.' };
    const episodeHandle = route.kind === 'space' ? route.episodeHandle : undefined;
    if (episodeHandle && !space.episodeHandles.includes(episodeHandle)) {
        return { error: 'This episode does not belong to the requested space.' };
    }
    return { spaceSlug, episodeHandle };
}
